/**
 * <SessionPickerStep> — l'étape « Quelle séance ? » (organism).
 *
 * Multi-lives est le cas normal : aucun geste ne suppose « la » prochaine séance, il demande
 * laquelle. Chaque ligne porte son tuteur, parce que multi-tuteurs l'est tout autant.
 *
 * **Une séance a une seule anatomie** : la ligne horaire, puis la ligne de la séance
 * (`<SessionDetailsLine>` — la pile de ses matières, « Maths · avec Camille · 1h »), comme à
 * l'accueil et dans « Tous mes lives ». Rien n'est calé à droite, sauf la coche de la sélection.
 *
 * `disabledReason` sert le garde-fou des 2 h : une séance trop proche reste visible et dit
 * pourquoi elle est refusée, au lieu de disparaître de la liste sans explication. Elle reste
 * choisissable quand le parcours sait quoi proposer ensuite (l'écran « trop tard »).
 *
 * `noteFor` ajoute à une ligne une règle qui ne vaut que pour elle, sous sa description — le
 * parcours de décalage y écrit l'heure limite d'une séance d'aujourd'hui qu'on peut encore décaler.
 */
import React from "react"

import { Avatar } from "../../atoms/Avatar"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { OptionCard } from "../../molecules/OptionCard"
import { SessionDetailsLine } from "../../molecules/SessionDetailsLine"
import { type UILiveSession } from "../../../ports/primary/ui/types"
import { formatDayTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import { tutorShortName } from "../../../ports/primary/ui/formatters/liveSessionFormatter"

export type SessionPickerStepProps = {
    sessions: UILiveSession[]
    selectedId?: UILiveSession["id"]
    onSelect: (session: UILiveSession) => void
    /** Étiquette « Trop tard » posée par le parcours sur les séances à moins de 2 h. */
    badgeFor?: (session: UILiveSession) => string | undefined
    /** Seconde ligne d'une séance — « Possible jusqu'à 15h19 ». */
    noteFor?: (session: UILiveSession) => string | undefined
    /** Texte quand il n'y a aucune séance à venir. */
    emptyMessage?: string
    testID?: string
}

export function SessionPickerStep(props: SessionPickerStepProps) {
    const {
        sessions,
        selectedId,
        onSelect,
        badgeFor,
        noteFor,
        emptyMessage = "Aucune séance à venir pour le moment.",
        testID,
    } = props

    if (sessions.length === 0) {
        return (
            <Text color="text-muted" testID={testID}>
                {emptyMessage}
            </Text>
        )
    }

    return (
        <Stack gap="2" testID={testID}>
            {sessions.map((session) => (
                <OptionCard
                    key={session.id}
                    label={formatDayTime(session.date)}
                    description={<SessionDetailsLine session={session} />}
                    note={noteFor?.(session)}
                    badge={badgeFor?.(session)}
                    badgeVariant="warning"
                    selected={session.id === selectedId}
                    onPress={() => onSelect(session)}
                    leading={
                        <Avatar uri={session.tutor.avatarUrl} name={tutorShortName(session.tutor)} size={44} signed />
                    }
                />
            ))}
        </Stack>
    )
}
